import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import { useAssistantSelector } from '@hellodarwin/app-features/lib/features/assistant/redux/assistant-hooks';
import {
  selectActiveConversationId,
  selectConversationMessages,
} from '@hellodarwin/app-features/lib/features/assistant/redux/selectors';
import { selectGrantStatisticsResponses } from '@hellodarwin/app-features/lib/features/program-candidates-data/redux/accepted-grants-slice';
import Collapse from '@hellodarwin/core/lib/components/common/Collapse';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import useMediaQuery from '@hellodarwin/core/lib/features/helpers/use-media-query';
import { usePlatform } from '@hellodarwin/core/lib/features/providers/platform-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import TabArrow from '@hellodarwin/icons/dist/icons/TabArrow';
import Progress from 'antd/es/progress';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useAppSelector } from '../../../../app/app-hooks';
import { useAppDispatch } from '../../../../app/index';
import { selectApplicationItemCreated } from '../../../../features/api/slices/gin-steps-slice';
import { selectAllGrantProfiles } from '../../../../features/api/slices/grant-profiles-slice';
import { selectCompleteProfileModal } from '../../../../features/api/slices/global-slice';
import { selectAllSubscribedNotifications } from '../../../../features/api/slices/grants-notifications-slice';
import { selectAllFundingExplorerGrants } from '../../../../features/api/slices/grants-slice';
import {
  fetchCompanyTourSteps,
  selectCompany,
  selectCompanyTourStatus,
  selectCompanyTourSteps,
  selectTourProgress,
} from '../../../../features/api/slices/profile-slice';
import { useClientApi } from '../../../../features/api/use-client-api';
import TourChecklistContent from '../TourChecklistContent';
import {
  ChecklistDropdownBody,
  ChecklistDropdownContainer,
  ChecklistDropdownHeader,
} from './style';

const TourChecklistDropdown: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslations();
  const isMobile = useMediaQuery({ maxWidth: theme.breakpoints.mobile });
  const { menuOpen } = usePlatform();
  const tourProgress = useAppSelector(selectTourProgress);
  const tourChecklist = useAppSelector(selectCompanyTourSteps);
  const tourChecklistStatus = useAppSelector(selectCompanyTourStatus);
  const company = useAppSelector(selectCompany);
  const dispatch = useAppDispatch();
  const clientApi = useClientApi();
  const { accessLevel } = usePremiumAccess();
  const activeConversationId = useAssistantSelector(selectActiveConversationId);
  const hasSentMessage = useAssistantSelector(
    (state) =>
      (selectConversationMessages(state, activeConversationId || '')?.length ??
        0) > 0,
  );
  const hasSearchGrants = useAppSelector(
    (state) => selectAllFundingExplorerGrants(state).length !== 0,
  );
  const isCompleteProfileModalOpen = useAppSelector(
    (state) => selectCompleteProfileModal(state).isOpen,
  );
  const hasSubscribedAlerts = useAppSelector(
    (state) => selectAllSubscribedNotifications(state).length > 0,
  );
  const applicationItemCreated = useAppSelector(selectApplicationItemCreated);
  const hasGrantProfiles = useAppSelector(
    (state) => selectAllGrantProfiles(state).length > 0,
  );
  const hasVisitedFundingIntelligence = useAppSelector(
    (state) => selectGrantStatisticsResponses(state).length > 0,
  );
  const location = useLocation();
  const tourCompleted =
    accessLevel === 'expert'
      ? company.expert_tour_completed_at
      : accessLevel === 'premium'
        ? company.pro_tour_completed_at
        : company.free_tour_completed_at;

  const [hasCompletedFadeOut, setHasCompletedFadeOut] = useState(
    () => !!tourCompleted,
  );

  const isFadingOut = !!tourCompleted && !hasCompletedFadeOut;
  const shouldRenderCollapseContent = tourChecklist.length > 0 || isFadingOut;
  const progressPercent = tourCompleted
    ? 100
    : tourChecklist.length === 0 && tourChecklistStatus !== 'pending'
      ? 100
      : tourProgress.percentage;

  useEffect(() => {
    if (tourCompleted) return;
    dispatch(fetchCompanyTourSteps({ api: clientApi }));
  }, [
    dispatch,
    clientApi,
    tourCompleted,
    hasSearchGrants,
    isCompleteProfileModalOpen,
    hasSubscribedAlerts,
    location.pathname,
    hasSentMessage,
    applicationItemCreated,
    hasVisitedFundingIntelligence,
    hasGrantProfiles,
  ]);

  useEffect(() => {
    dispatch(fetchCompanyTourSteps({ api: clientApi }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourCompleted]);
  if (hasCompletedFadeOut || menuOpen) {
    return null;
  }

  return (
    <ChecklistDropdownContainer
      $isMobile={isMobile}
      $fadeOut={isFadingOut}
      className="notranslate"
      id="tour-checklist-dropdown"
      translate="no"
      onAnimationEnd={() => {
        if (isFadingOut) {
          setHasCompletedFadeOut(true);
        }
      }}
    >
      <Collapse
        ghost={!isMobile}
        accordion
        defaultActiveKey={
          !isMobile && location.pathname === '/dashboard'
            ? 'checklist'
            : undefined
        }
        items={[
          {
            id: 'checklist',
            title: (
              <ChecklistDropdownHeader>
                <Typography elementTheme="h6" color={theme.colors.grey_1}>
                  {accessLevel === 'premium'
                    ? t('dashboardChecklist|title_pro')
                    : t('dashboardChecklist|title')}
                  {tourProgress.total > 0 &&
                    ` (${tourProgress.completed}/${tourProgress.total})`}
                </Typography>
                <Progress
                  percent={progressPercent}
                  strokeColor={theme.colors.green_1}
                  style={{ width: '100%' }}
                />
              </ChecklistDropdownHeader>
            ),
            children: shouldRenderCollapseContent ? (
              <ChecklistDropdownBody>
                <TourChecklistContent
                  tourChecklist={tourChecklist}
                  showTitle={false}
                  showProgress={false}
                  isLoading={tourChecklistStatus === 'pending'}
                />
              </ChecklistDropdownBody>
            ) : undefined,
          },
        ]}
        ExpandIcon={({ isActive }) =>
          shouldRenderCollapseContent ? (
            <TabArrow size={16} down={isActive} color={theme.colors.grey_2} />
          ) : null
        }
        expandIconPosition="end"
        styles={{
          container: {
            paddingTop: !isMobile ? theme.spacing.md : 0,
            background: theme.colors.white_1,
            border: !isMobile ? `1px solid ${theme.colors.grey_3}` : 'none',
            minWidth: 'min(400px, 100vw)',
            maxWidth: '100vw',
            width: isMobile ? '100%' : 'fit-content',
          },
        }}
      />
    </ChecklistDropdownContainer>
  );
};

export default TourChecklistDropdown;
